"use client";

import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PhosphorIcon, {
  type PhosphorIconName,
} from "@/shared/components/common/PhosphorIcon";
import type {
  HotelResultsV2AmenityItem,
  HotelResultsV2Filters,
  HotelResultsV2OfferItem,
} from "../adapters/hotelResultsV2.model";
import { formatHotelResultsV2Price, toHotelResultsV2FaNumber } from "../utils";

interface FilterSidebarProps {
  filters: HotelResultsV2Filters;
  counts: HotelResultsV2FilterCounts;
  areas: string[];
  amenityOptions: HotelResultsV2AmenityItem[];
  offerOptions: HotelResultsV2OfferItem[];
  priceRange: {
    min: number;
    max: number;
  };
  priceSamples: number[];
  onChange: (filters: Partial<HotelResultsV2Filters>) => void;
  onReset: () => void;
  mobile?: boolean;
}

export type HotelResultsV2FilterCounts = {
  stars: Record<number, number>;
  amenities: Record<string, number>;
  offers: Record<string, number>;
  mealPlan: Record<string, number>;
  areas: Record<string, number>;
  score: Record<number, number>;
  services: {
    breakfast: number;
    freeCancellation: number;
  };
  types: Record<(typeof HOTEL_TYPE_ITEMS)[number]["key"], number>;
};

const PRICE_BUCKET_SIZE = 500_000;
const FALLBACK_PRICE_BUCKETS = 6;
const FILTER_TEXT_MIN_LENGTH = 3;
const FILTER_TEXT_DEBOUNCE_MS = 650;
const AMENITIES_VISIBLE_LIMIT = 5;
const PRIORITY_AMENITY_LABELS = [
  "لابی",
  "پذیرش 24 ساعته",
  "پذیرش ۲۴ ساعته",
  "آسانسور",
  "امکان شارژ وسایل الکترونیکی",
  "شارژ وسایل الکترونیکی",
  "صندوق امانات",
];

const MEAL_OPTIONS = [
  { key: "room_only", label: "اقامت تک" },
  { key: "breakfast", label: "صبحانه" },
  { key: "full", label: "فولبرد" },
];

const HOTEL_TYPE_ITEMS = [
  { key: "isPopular", label: "پیشنهاد ویژه", icon: "Star" },
] as const;

export default function FilterSidebar({
  filters,
  counts,
  areas,
  amenityOptions,
  offerOptions,
  priceRange,
  priceSamples,
  onChange,
  onReset,
  mobile,
}: FilterSidebarProps) {
  const [hotelNameQuery, setHotelNameQuery] = useState(filters.hotelName);
  const [amenityQuery, setAmenityQuery] = useState("");
  const debouncedHotelNameQuery = useDebouncedValue(
    hotelNameQuery,
    FILTER_TEXT_DEBOUNCE_MS,
  );
  const debouncedAmenityQuery = useDebouncedValue(
    amenityQuery,
    FILTER_TEXT_DEBOUNCE_MS,
  );
  const [showAllAreas, setShowAllAreas] = useState(false);
  const activeAmenityQuery =
    debouncedAmenityQuery.trim().length >= FILTER_TEXT_MIN_LENGTH
      ? debouncedAmenityQuery.trim()
      : "";
  const filteredAmenityItems = useMemo(
    () =>
      getPrioritizedAmenityItems(
        amenityOptions.filter((item) =>
          item.label.includes(activeAmenityQuery),
        ),
      ),
    [activeAmenityQuery, amenityOptions],
  );
  const prioritizedAmenityOptions = useMemo(
    () => getPrioritizedAmenityItems(amenityOptions),
    [amenityOptions],
  );
  const visibleAmenityItems = (
    activeAmenityQuery ? filteredAmenityItems : prioritizedAmenityOptions
  ).slice(0, AMENITIES_VISIBLE_LIMIT);
  const visibleAreas = showAllAreas ? areas : areas.slice(0, 5);
  const hasAmenityFilters = amenityOptions.some(
    (item) => (counts.amenities[item.key] ?? 0) > 0,
  );
  const hasOfferFilters = offerOptions.some(
    (item) => (counts.offers[item.key] ?? 0) > 0,
  );
  const toggleStar = (star: number) => {
    onChange({
      stars: filters.stars.includes(star)
        ? filters.stars.filter((item) => item !== star)
        : [...filters.stars, star],
    });
  };

  const toggleMeal = (meal: string) => {
    onChange({
      mealPlan: filters.mealPlan.includes(meal)
        ? filters.mealPlan.filter((item) => item !== meal)
        : [...filters.mealPlan, meal],
    });
  };

  const toggleArea = (area: string) => {
    onChange({
      areas: filters.areas.includes(area)
        ? filters.areas.filter((item) => item !== area)
        : [...filters.areas, area],
    });
  };

  const activeChips = buildActiveFilterChips(filters, onChange, {
    toggleStar,
    toggleMeal,
    toggleArea,
    amenityOptions,
    offerOptions,
    minPrice: priceRange.min,
    maxPrice: priceRange.max,
  });

  useEffect(() => {
    setHotelNameQuery(filters.hotelName);
  }, [filters.hotelName]);

  useEffect(() => {
    const nextHotelName = debouncedHotelNameQuery.trim();

    if (nextHotelName.length === 0) {
      if (filters.hotelName) onChange({ hotelName: "" });
      return;
    }

    if (nextHotelName.length < FILTER_TEXT_MIN_LENGTH) return;
    if (nextHotelName !== filters.hotelName) {
      onChange({ hotelName: nextHotelName });
    }
  }, [debouncedHotelNameQuery, filters.hotelName, onChange]);

  return (
    <aside
      className={`overflow-x-clip bg-white [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${
        mobile
          ? "max-h-none overflow-y-auto rounded-none p-0 shadow-none"
          : "overflow-visible rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
      }`}
      dir="rtl"
    >
      <div className="mb-4 flex h-8 items-center justify-between">
        <div className="text-base font-bold text-slate-950">فیلترها</div>
        <button
          onClick={onReset}
          className={`rounded-full px-2.5 py-1 text-sm font-medium text-primary transition hover:bg-primary/5 ${
            activeChips.length > 0
              ? "opacity-100"
              : "pointer-events-none opacity-0"
          }`}
          type="button"
          aria-hidden={activeChips.length === 0}
          tabIndex={activeChips.length > 0 ? 0 : -1}
        >
          حذف همه
        </button>
      </div>

      <div className="mb-4 h-10 overflow-x-auto overflow-y-hidden whitespace-nowrap border-b border-slate-100 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div
          className={`inline-flex gap-1.5 transition-opacity ${
            activeChips.length > 0
              ? "opacity-100"
              : "pointer-events-none opacity-0"
          }`}
        >
          {activeChips.map((chip) => (
            <span
              key={chip.id}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm text-slate-700"
            >
              {chip.label}
              <button
                onClick={chip.remove}
                className="text-slate-400 transition hover:text-red-500"
                type="button"
                aria-label={`حذف ${chip.label}`}
              >
                <PhosphorIcon name="X" size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="mb-4 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-3 ring-1 ring-transparent transition focus-within:bg-white focus-within:ring-primary/25">
          <PhosphorIcon
            name="MagnifyingGlass"
            size={16}
            className="shrink-0 text-slate-400"
          />
          <input
            value={hotelNameQuery}
            onChange={(event) => setHotelNameQuery(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-base text-slate-950 outline-none placeholder:text-slate-400"
            placeholder="جستجوی نام هتل..."
          />
          {hotelNameQuery && (
            <button
              onClick={() => {
                setHotelNameQuery("");
                onChange({ hotelName: "" });
              }}
              type="button"
              aria-label="پاک کردن نام هتل"
            >
              <PhosphorIcon name="X" size={14} className="text-slate-400" />
            </button>
          )}
        </div>
      </div>

      <FilterSection title="بازه قیمتی">
        <PriceRangeFilter
          minPrice={filters.minPrice}
          maxPrice={filters.maxPrice}
          minLimit={priceRange.min}
          maxLimit={priceRange.max}
          priceSamples={priceSamples}
          onChange={onChange}
        />
      </FilterSection>

      <FilterSection title="ستاره هتل">
        <div className="grid grid-cols-4 gap-1.5">
          {[5, 4, 3, 2].map((star) => {
            const isActive = filters.stars.includes(star);

            return (
              <button
                key={star}
                onClick={() => toggleStar(star)}
                className={`inline-flex items-center justify-center gap-1 rounded-full border px-2 py-2 text-xs transition ${
                  isActive
                    ? "border-primary bg-primary text-white"
                    : "border-slate-200 text-slate-700 hover:border-primary"
                }`}
                type="button"
              >
                {toHotelResultsV2FaNumber(star)}
                <PhosphorIcon
                  name="Star"
                  weight="fill"
                  size={13}
                  className={isActive ? "text-white" : "text-amber-400"}
                />
                <CountBadge count={counts.stars[star] ?? 0} active={isActive} />
              </button>
            );
          })}
        </div>
      </FilterSection>

      {hasOfferFilters && (
        <FilterSection title="آفرها">
          {offerOptions.map((item) => (
            <CheckFilter
              key={item.key}
              label={item.label}
              icon={item.icon}
              checked={filters.offers.includes(item.key)}
              count={counts.offers[item.key] ?? 0}
              onChange={() =>
                onChange({
                  offers: filters.offers.includes(item.key)
                    ? filters.offers.filter((offerKey) => offerKey !== item.key)
                    : [...filters.offers, item.key],
                })
              }
            />
          ))}
        </FilterSection>
      )}

      {hasAmenityFilters && (
        <FilterSection title="امکانات">
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 ring-1 ring-transparent transition focus-within:bg-white focus-within:ring-primary/25">
            <PhosphorIcon
              name="MagnifyingGlass"
              size={14}
              className="shrink-0 text-slate-400"
            />
            <input
              value={amenityQuery}
              onChange={(event) => setAmenityQuery(event.target.value)}
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-950 outline-none placeholder:text-slate-400"
              placeholder="جستجوی امکانات..."
            />
            {amenityQuery && (
              <button
                onClick={() => setAmenityQuery("")}
                type="button"
                aria-label="پاک کردن جستجوی امکانات"
              >
                <PhosphorIcon name="X" size={13} className="text-slate-400" />
              </button>
            )}
          </div>
          {visibleAmenityItems.map((item) => (
            <CheckFilter
              key={item.key}
              label={item.label}
              icon={item.icon}
              checked={filters.amenities.includes(item.key)}
              count={counts.amenities[item.key] ?? 0}
              onChange={() =>
                onChange({
                  amenities: filters.amenities.includes(item.key)
                    ? filters.amenities.filter(
                        (amenityKey) => amenityKey !== item.key,
                      )
                    : [...filters.amenities, item.key],
                })
              }
            />
          ))}
        </FilterSection>
      )}

      {/* Kept for when meal-plan data becomes reliable in the results API. */}
      {/* <FilterSection title="وعده‌های غذایی" defaultOpen={false}>
        {MEAL_OPTIONS.map((item) => (
          <CheckFilter
            key={item.key}
            label={item.label}
            icon="Coffee"
            checked={filters.mealPlan.includes(item.key)}
            count={counts.mealPlan[item.key] ?? 0}
            onChange={() => toggleMeal(item.key)}
          />
        ))}
      </FilterSection> */}

      {/* Kept for when hotel area/location taxonomy is available from the API. */}
      {/* <FilterSection title="محدوده اطراف" defaultOpen={false}>
        <div className="space-y-1.5">
          {visibleAreas.map((area) => (
            <CheckFilter
              key={area}
              label={area}
              icon="MapPin"
              checked={filters.areas.includes(area)}
              count={counts.areas[area] ?? 0}
              onChange={() => toggleArea(area)}
            />
          ))}
        </div>
        {areas.length > 5 && (
          <ShowMoreFilterButton
            expanded={showAllAreas}
            onClick={() => setShowAllAreas((value) => !value)}
          />
        )}
      </FilterSection> */}

      {/* Kept for when real guest score/review data is consistently available. */}
      {/* <FilterSection title="امتیاز مهمانان" defaultOpen={false}>
        <div className="space-y-2">
          {[9, 8, 7].map((score) => (
            <RadioFilter
              key={score}
              label={getScoreLabel(score)}
              checked={filters.minScore === score}
              count={counts.score[score] ?? 0}
              onChange={() =>
                onChange({ minScore: filters.minScore === score ? 0 : score })
              }
            />
          ))}
        </div>
      </FilterSection> */}

      {/* Kept for when hotel type/category flags are supplied by backend data. */}
      {/* <FilterSection title="نوع هتل" defaultOpen={false}>
        {HOTEL_TYPE_ITEMS.map((item) => (
          <CheckFilter
            key={item.key}
            label={item.label}
            icon={item.icon}
            checked={Boolean(filters[item.key])}
            count={counts.types[item.key] ?? 0}
            onChange={() =>
              onChange({
                [item.key]: !filters[item.key],
              })
            }
          />
        ))}
      </FilterSection> */}
    </aside>
  );
}

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="mb-5 border-b border-slate-100 pb-5 last:mb-0 last:border-b-0 last:pb-0">
      <button
        className="mb-3 flex w-full items-center justify-between text-right"
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <span className="text-base font-semibold text-slate-950">{title}</span>
        <PhosphorIcon
          name={open ? "CaretUp" : "CaretDown"}
          size={17}
          className="text-slate-400"
        />
      </button>
      {open && <div className="space-y-2">{children}</div>}
    </section>
  );
}

function PriceRangeFilter({
  minPrice,
  maxPrice,
  minLimit,
  maxLimit,
  priceSamples,
  onChange,
}: {
  minPrice: number;
  maxPrice: number;
  minLimit: number;
  maxLimit: number;
  priceSamples: number[];
  onChange: (filters: Partial<HotelResultsV2Filters>) => void;
}) {
  const histogram = useMemo(
    () => buildPriceHistogram(priceSamples, minLimit, maxLimit),
    [maxLimit, minLimit, priceSamples],
  );
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"min" | "max" | null>(null);
  const [draftRange, setDraftRange] = useState({
    minPrice,
    maxPrice,
  });
  const draftRangeRef = useRef(draftRange);
  const maxCount = Math.max(...histogram.map((bucket) => bucket.count), 1);
  const safeRange = Math.max(1, maxLimit - minLimit);
  const minPct = ((draftRange.minPrice - minLimit) / safeRange) * 100;
  const maxPct = ((draftRange.maxPrice - minLimit) / safeRange) * 100;
  const minLeft = clampPercent(minPct);
  const maxLeft = clampPercent(maxPct);
  const commitRange = useCallback(
    (range = draftRangeRef.current) => {
      onChange({
        minPrice: range.minPrice,
        maxPrice: range.maxPrice,
      });
    },
    [onChange],
  );
  const updateDraggedPrice = useCallback(
    (value: number, target: "min" | "max") => {
      if (target === "min") {
        setDraftRange((current) => {
          const next = {
            ...current,
            minPrice: Math.min(value, current.maxPrice - 100_000),
          };
          draftRangeRef.current = next;
          return next;
        });
        return;
      }

      setDraftRange((current) => {
        const next = {
          ...current,
          maxPrice: Math.max(value, current.minPrice + 100_000),
        };
        draftRangeRef.current = next;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (dragging) return;
    const next = { minPrice, maxPrice };
    draftRangeRef.current = next;
    setDraftRange(next);
  }, [dragging, maxPrice, minPrice]);

  useEffect(() => {
    draftRangeRef.current = draftRange;
  }, [draftRange]);

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (event: PointerEvent) => {
      const value = getPriceFromClientX(
        event.clientX,
        trackRef.current,
        minLimit,
        maxLimit,
      );

      updateDraggedPrice(value, dragging);
    };

    const handleUp = () => {
      setDragging(null);
      commitRange();
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [commitRange, dragging, maxLimit, minLimit, updateDraggedPrice]);

  const handleTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button")) return;

    const value = getPriceFromClientX(
      event.clientX,
      trackRef.current,
      minLimit,
      maxLimit,
    );
    const target =
      Math.abs(value - draftRange.minPrice) <=
      Math.abs(value - draftRange.maxPrice)
        ? "min"
        : "max";

    updateDraggedPrice(value, target);
    setDragging(target);
  };

  return (
    <div className="w-full min-w-0 overflow-x-clip">
      <div
        className="grid h-12 w-full min-w-0 items-end gap-px overflow-x-clip"
        dir="ltr"
        style={{
          gridTemplateColumns: `repeat(${histogram.length}, minmax(0, 1fr))`,
        }}
      >
        {histogram.map((count, index) => {
          const bucketCenter = count.min + (count.max - count.min) / 2;
          const isInRange =
            bucketCenter >= draftRange.minPrice &&
            bucketCenter <= draftRange.maxPrice;
          const height = Math.max(8, (count.count / maxCount) * 100);

          return (
            <span
              key={`${count.min}-${count.max}-${index}`}
              className="group relative min-w-0 rounded-t-sm transition-colors duration-150"
              title={`${formatHotelResultsV2Price(count.min)} تا ${formatHotelResultsV2Price(count.max)} تومان - ${toHotelResultsV2FaNumber(count.count)} هتل`}
              style={{
                height: `${height}%`,
                backgroundColor: isInRange
                  ? "color-mix(in oklch, var(--primary) 46%, white)"
                  : "color-mix(in oklch, var(--primary) 14%, white)",
              }}
            >
              <span
                className={`pointer-events-none absolute bottom-[calc(100%+6px)] z-20 hidden w-24 rounded-lg bg-slate-950 px-2 py-1 text-center text-[10px] font-semibold leading-5 text-white opacity-0 shadow-lg transition-opacity duration-75 group-hover:opacity-100 sm:block ${getHistogramTooltipPlacement(
                  index,
                  histogram.length,
                )}`}
                dir="rtl"
              >
                {formatCompactPrice(count.min)} تا{" "}
                {formatCompactPrice(count.max)}
                <br />
                {toHotelResultsV2FaNumber(count.count)} هتل
              </span>
            </span>
          );
        })}
      </div>

      <div className="relative mt-1 h-8 touch-none px-3" dir="ltr">
        <div
          ref={trackRef}
          className="relative h-full cursor-pointer"
          onPointerDown={handleTrackPointerDown}
        >
          <div className="absolute top-1/2 h-1.5 w-full -translate-y-1/2 rounded-full bg-slate-200" />
          <div
            className="pointer-events-none absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-primary"
            style={{ left: `${minLeft}%`, right: `${100 - maxLeft}%` }}
          />
          <button
            className="absolute top-1/2 z-50 size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-white shadow-md transition hover:scale-110"
            style={{ left: `${minLeft}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              setDragging("min");
            }}
            type="button"
            aria-label="تغییر کف قیمت"
          >
            <span className="mx-auto block size-2 rounded-full bg-primary" />
          </button>
          <button
            className="absolute top-1/2 z-50 size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-white shadow-md transition hover:scale-110"
            style={{ left: `${maxLeft}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              setDragging("max");
            }}
            type="button"
            aria-label="تغییر سقف قیمت"
          >
            <span className="mx-auto block size-2 rounded-full bg-primary" />
          </button>
        </div>
      </div>

      <div className="mt-2 flex justify-between text-xs text-slate-500">
        <span>{formatHotelResultsV2Price(draftRange.maxPrice)} تومان</span>
        <span>{formatHotelResultsV2Price(draftRange.minPrice)} تومان</span>
      </div>
    </div>
  );
}

function CheckFilter({
  label,
  icon,
  checked,
  count,
  onChange,
}: {
  label: string;
  icon: PhosphorIconName;
  checked: boolean;
  count: number;
  onChange: () => void;
}) {
  return (
    <button
      className="group flex w-full cursor-pointer items-center gap-3 rounded-md py-1 text-right transition hover:bg-slate-50"
      onClick={onChange}
      type="button"
    >
      <span
        className={`grid size-5 shrink-0 place-items-center rounded border-2 transition-colors ${
          checked
            ? "border-primary bg-primary"
            : "border-slate-200 group-hover:border-primary"
        }`}
      >
        {checked && (
          <PhosphorIcon name="Check" size={12} className="text-white" />
        )}
      </span>
      <PhosphorIcon name={icon} size={17} className="text-slate-400" />
      <span className="min-w-0 flex-1 text-base text-slate-700">{label}</span>
      <CountBadge count={count} active={checked} />
    </button>
  );
}

function RadioFilter({
  label,
  checked,
  count,
  onChange,
}: {
  label: string;
  checked: boolean;
  count: number;
  onChange: () => void;
}) {
  return (
    <button
      className="group flex w-full items-center gap-3 rounded-md py-1 text-right transition hover:bg-slate-50"
      onClick={onChange}
      type="button"
    >
      <span
        className={`grid size-5 shrink-0 place-items-center rounded-full border-2 transition-colors ${
          checked
            ? "border-primary bg-primary"
            : "border-slate-200 group-hover:border-primary"
        }`}
      >
        {checked && <span className="size-2.5 rounded-full bg-white" />}
      </span>
      <span className="min-w-0 flex-1 text-base text-slate-700">{label}</span>
      <CountBadge count={count} active={checked} />
    </button>
  );
}

function CountBadge({ count, active }: { count: number; active?: boolean }) {
  return (
    <span
      className={`inline-flex min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold ${
        active ? "bg-white/20 text-current" : "bg-slate-100 text-slate-500"
      }`}
    >
      {toHotelResultsV2FaNumber(count)}
    </span>
  );
}

function ShowMoreFilterButton({
  expanded,
  onClick,
}: {
  expanded: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="mt-2 flex items-center gap-1 text-sm font-semibold text-primary transition hover:opacity-80"
      type="button"
    >
      {expanded ? "نمایش کمتر" : "نمایش بیشتر"}
      <PhosphorIcon name={expanded ? "CaretUp" : "CaretDown"} size={13} />
    </button>
  );
}

function buildPriceHistogram(
  priceSamples: number[],
  minLimit: number,
  maxLimit: number,
) {
  const samples = priceSamples.filter(
    (price) => Number.isFinite(price) && price > 0,
  );
  const bucketCount = Math.max(
    1,
    Math.ceil((maxLimit - minLimit) / PRICE_BUCKET_SIZE) ||
      FALLBACK_PRICE_BUCKETS,
  );
  const bucketSize = PRICE_BUCKET_SIZE;
  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const min = minLimit + index * bucketSize;

    return {
      min,
      max: Math.min(min + bucketSize, maxLimit),
      count: 0,
    };
  });

  if (!samples.length) return buckets;

  samples.forEach((price) => {
    const bucket = Math.min(
      Math.floor((price - minLimit) / bucketSize),
      bucketCount - 1,
    );

    buckets[Math.max(0, bucket)].count += 1;
  });

  return buckets;
}

function clampPercent(value: number) {
  return Math.max(0, Math.min(100, value));
}

function getHistogramTooltipPlacement(index: number, total: number) {
  if (index <= 1) return "left-0 translate-x-0";
  if (index >= total - 2) return "right-0 translate-x-0";

  return "left-1/2 -translate-x-1/2";
}

function formatCompactPrice(price: number) {
  if (price >= 1_000_000) {
    const value = price / 1_000_000;
    const compact = Number.isInteger(value)
      ? value.toString()
      : value.toFixed(1);

    return `${toHotelResultsV2FaNumber(compact)} م`;
  }

  return `${toHotelResultsV2FaNumber(Math.round(price / 1000).toString())} ه`;
}

function getPriceFromClientX(
  clientX: number,
  trackElement: HTMLDivElement | null,
  minLimit: number,
  maxLimit: number,
) {
  if (!trackElement) return minLimit;

  const rect = trackElement.getBoundingClientRect();
  const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const rawValue = minLimit + ratio * Math.max(1, maxLimit - minLimit);

  return Math.max(
    minLimit,
    Math.min(maxLimit, Math.round(rawValue / 100_000) * 100_000),
  );
}

function getScoreLabel(score: number) {
  if (score === 9) return "عالی (۹+)";
  if (score === 8) return "خیلی خوب (۸+)";
  return "خوب (۷+)";
}

function getPrioritizedAmenityItems(items: HotelResultsV2AmenityItem[]) {
  return [...items].sort((first, second) => {
    const firstPriority = getAmenityPriority(first.label);
    const secondPriority = getAmenityPriority(second.label);

    if (firstPriority !== secondPriority) {
      return firstPriority - secondPriority;
    }

    return 0;
  });
}

function getAmenityPriority(label: string) {
  const normalizedLabel = normalizeAmenityLabel(label);
  const index = PRIORITY_AMENITY_LABELS.findIndex(
    (priorityLabel) => normalizeAmenityLabel(priorityLabel) === normalizedLabel,
  );

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function normalizeAmenityLabel(label: string) {
  return label
    .replace(/[ي]/g, "ی")
    .replace(/[ك]/g, "ک")
    .replace(/[۰٠]/g, "0")
    .replace(/[۱١]/g, "1")
    .replace(/[۲٢]/g, "2")
    .replace(/[۳٣]/g, "3")
    .replace(/[۴٤]/g, "4")
    .replace(/[۵٥]/g, "5")
    .replace(/[۶٦]/g, "6")
    .replace(/[۷٧]/g, "7")
    .replace(/[۸٨]/g, "8")
    .replace(/[۹٩]/g, "9")
    .replace(/\s+/g, " ")
    .trim();
}

function buildActiveFilterChips(
  filters: HotelResultsV2Filters,
  onChange: (filters: Partial<HotelResultsV2Filters>) => void,
  toggles: {
    toggleStar: (star: number) => void;
    toggleMeal: (meal: string) => void;
    toggleArea: (area: string) => void;
    amenityOptions: HotelResultsV2AmenityItem[];
    offerOptions: HotelResultsV2OfferItem[];
    minPrice: number;
    maxPrice: number;
  },
) {
  const chips: Array<{ id: string; label: string; remove: () => void }> = [];

  if (filters.hotelName) {
    chips.push({
      id: "hotelName",
      label: `"${filters.hotelName}"`,
      remove: () => onChange({ hotelName: "" }),
    });
  }
  if (filters.minPrice > toggles.minPrice) {
    chips.push({
      id: "minPrice",
      label: `از ${formatHotelResultsV2Price(filters.minPrice)}`,
      remove: () => onChange({ minPrice: toggles.minPrice }),
    });
  }
  if (filters.maxPrice < toggles.maxPrice) {
    chips.push({
      id: "maxPrice",
      label: `تا ${formatHotelResultsV2Price(filters.maxPrice)}`,
      remove: () => onChange({ maxPrice: toggles.maxPrice }),
    });
  }
  filters.stars.forEach((star) => {
    chips.push({
      id: `star-${star}`,
      label: `${toHotelResultsV2FaNumber(star)} ستاره`,
      remove: () => toggles.toggleStar(star),
    });
  });
  if (filters.minScore > 0) {
    chips.push({
      id: "minScore",
      label: `امتیاز ${toHotelResultsV2FaNumber(filters.minScore)}+`,
      remove: () => onChange({ minScore: 0 }),
    });
  }
  filters.amenities.forEach((amenityKey) => {
    const label =
      toggles.amenityOptions.find((item) => item.key === amenityKey)?.label ??
      amenityKey;

    chips.push({
      id: `amenity-${amenityKey}`,
      label,
      remove: () =>
        onChange({
          amenities: filters.amenities.filter((item) => item !== amenityKey),
        }),
    });
  });
  filters.offers.forEach((offerKey) => {
    const label =
      toggles.offerOptions.find((item) => item.key === offerKey)?.label ??
      offerKey;

    chips.push({
      id: `offer-${offerKey}`,
      label,
      remove: () =>
        onChange({
          offers: filters.offers.filter((item) => item !== offerKey),
        }),
    });
  });
  if (filters.breakfast)
    addChip(chips, "breakfast", "صبحانه", () => onChange({ breakfast: false }));
  if (filters.freeCancellation)
    addChip(chips, "freeCancellation", "کنسلی رایگان", () =>
      onChange({ freeCancellation: false }),
    );
  if (filters.isPopular)
    addChip(chips, "isPopular", "پیشنهاد ویژه", () =>
      onChange({ isPopular: false }),
    );

  filters.mealPlan.forEach((meal) => {
    chips.push({
      id: `meal-${meal}`,
      label: MEAL_OPTIONS.find((item) => item.key === meal)?.label ?? meal,
      remove: () => toggles.toggleMeal(meal),
    });
  });
  filters.areas.forEach((area) => {
    chips.push({
      id: `area-${area}`,
      label: area,
      remove: () => toggles.toggleArea(area),
    });
  });

  return chips;
}

function addChip(
  chips: Array<{ id: string; label: string; remove: () => void }>,
  id: string,
  label: string,
  remove: () => void,
) {
  chips.push({ id, label, remove });
}

function useDebouncedValue<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);

    return () => window.clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
}
